// V1 source: customer/midas/components/DiagramHotspotRect.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 hotspot SVG group — Tier A behavioral parity with V1. Logic copied
// verbatim: color/bg/label utils, opacity dimming when other hotspots are
// highlighted, pulse animation on the active step, label-pill positioning.
//
// V1 had no MUI to scrub here — pure SVG. V2 is byte-equivalent except for
// the file name + the V2 file-header convention.

import React, { useCallback, useMemo } from "react";
import { DiagramHotspot } from "../../../../shared/chatBot/models/chatBotModels";
import {
  getDiagramColor,
  getDiagramBgColor,
  getDiagramLabelColors,
} from "../../utils/diagramColorMap";

interface Props {
  hotspot: DiagramHotspot;
  isHighlighted: boolean;
  isActive: boolean;
  /** true when at least one hotspot in the set is highlighted */
  hasAnyHighlighted: boolean;
  onMouseEnter: (id: string, e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  onClick: (id: string) => void;
}

const DiagramHotspotRectV2: React.FC<Props> = ({
  hotspot,
  isHighlighted,
  isActive,
  hasAnyHighlighted,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onClick,
}) => {
  const color = getDiagramColor(hotspot.type);
  const bgColor = getDiagramBgColor(hotspot.type);
  const labelColors = useMemo(() => getDiagramLabelColors(hotspot.type), [hotspot.type]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => onMouseEnter(hotspot.vi_id, e),
    [hotspot.vi_id, onMouseEnter]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick(hotspot.vi_id);
    },
    [hotspot.vi_id, onClick]
  );

  // Determine opacity: highlighted = full, non-highlighted-when-some-highlighted = dimmed
  const groupOpacity = hasAnyHighlighted && !isHighlighted ? 0.35 : 1;

  // Stroke and fill based on highlight state
  const strokeWidth = isHighlighted ? 0.4 : 0.25;
  const fillColor = isHighlighted ? (color + '40') : bgColor; // 25% vs 12%
  const filter = isHighlighted ? `drop-shadow(0 0 0.5px ${color})` : undefined;

  // Label positioning: pill just above the rect
  const labelX = hotspot.x_pct;
  const labelY = hotspot.y_pct - 0.3; // slightly above top edge
  const labelText = hotspot.label || hotspot.component_id || '';

  // Approximate label pill width (percentage-based, ~0.55% per char, min 2.5%)
  const pillWidth = Math.max(labelText.length * 0.55, 2.5);
  const pillHeight = 1.6;

  return (
    <g
      onMouseEnter={handleMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      style={{ cursor: "pointer", opacity: groupOpacity }}
      filter={filter}
    >
      {/* Main rectangle */}
      <rect
        x={`${hotspot.x_pct}%`}
        y={`${hotspot.y_pct}%`}
        width={`${hotspot.w_pct}%`}
        height={`${hotspot.h_pct}%`}
        fill={fillColor}
        stroke={color}
        strokeWidth={strokeWidth}
        rx="0"
        ry="0"
      />

      {/* Pulse animation for active step */}
      {isActive && (
        <rect
          x={`${hotspot.x_pct}%`}
          y={`${hotspot.y_pct}%`}
          width={`${hotspot.w_pct}%`}
          height={`${hotspot.h_pct}%`}
          fill="none"
          stroke={color}
          strokeWidth={0.6}
          rx="0"
          ry="0"
        >
          <animate
            attributeName="stroke-opacity"
            values="0.3;0.8;0.3"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </rect>
      )}

      {/* Label pill background */}
      {labelText && (
        <rect
          x={`${labelX}%`}
          y={`${labelY - pillHeight}%`}
          width={`${pillWidth}%`}
          height={`${pillHeight}%`}
          fill={labelColors.bg}
          rx="0.3"
          ry="0.3"
        />
      )}

      {/* Label text */}
      {labelText && (
        <text
          x={`${labelX + pillWidth / 2}%`}
          y={`${labelY - pillHeight / 2}%`}
          textAnchor="middle"
          dominantBaseline="central"
          fill={labelColors.fg}
          fontSize="0.9"
          fontFamily="'JetBrains Mono', 'Consolas', monospace"
          fontWeight="700"
        >
          {labelText}
        </text>
      )}
    </g>
  );
};

export default React.memo(DiagramHotspotRectV2);
